/**
 * useCoordinatedAutoSave — debounced autosave that is visible to the approval
 * flush rail.
 *
 * Three things have to happen together for an autosaving proposal editor to be
 * correct, and every one of them is a silent-data-loss bug when it is missed:
 *
 *   1. **Registration.** The approval footers call `flushAllAndWait()` right
 *      before the approve POST. An editor that is not registered lets the user
 *      change a field and approve inside the debounce window, and the proposal
 *      then executes with the stale payload.
 *   2. **Freeze.** Once the proposal leaves `pending` the backend rejects
 *      `update_payload` with `UPDATE_FAILED`, so no save may be armed after the
 *      freeze and no already-armed timer may fire through it. Both points are
 *      checked here (arm time in `save`, fire time via `useAutoSave`'s
 *      `shouldSave` gate).
 *   3. **Change detection.** Only a payload that differs from the last one we
 *      persisted is written, so reverting an edit (Escape) cancels the write
 *      instead of round-tripping the value the user just discarded.
 *
 * The debounce itself is `useAutoSave` — this hook adds the coordination wiring
 * around it rather than growing a fourth hand-rolled timer.
 */

import { useCallback, useEffect, useRef } from 'react';

import { useAutoSave } from './useAutoSave';
import { useAutoSaveCoordinationOptional } from './useAutoSaveCoordination';

/** Matches the debounce the other proposal autosave editors use. */
const DEFAULT_DEBOUNCE_MS = 2000;

type CoordinationHandle = ReturnType<
  NonNullable<ReturnType<typeof useAutoSaveCoordinationOptional>>['register']
>;

type CoordinationSaveState = 'idle' | 'pending' | 'saving' | 'error';

export type CoordinatedSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseCoordinatedAutoSaveOptions<T> {
  /**
   * Stable identity for the coordination registry. Include the action id so
   * two cards in one proposal never collide (e.g. `property-update-42`).
   */
  registrationId: string;
  /** Persists the value. Rejecting marks the save errored. */
  onSave: (value: T) => Promise<void>;
  /**
   * The value already persisted server-side when the editor mounts. Seeds
   * change detection so reverting to it does not issue a no-op write.
   */
  initialSavedValue?: T;
  /** When false, `save` and `flush` are no-ops. Default: true. */
  enabled?: boolean;
  /** Debounce delay before an armed save fires. Default: 2000ms. */
  debounceMs?: number;
}

export interface UseCoordinatedAutoSaveResult<T> {
  /** Arm a debounced save for `value`. Supersedes any already-armed save. */
  save: (value: T) => void;
  /**
   * Cancel the debounce and persist the pending value now. Registered as the
   * coordination flush function; also called on an explicit commit
   * (Enter / blur / select) so the edit lands without waiting out the debounce.
   * Rejects when the save fails, so `flushAllAndWait` can block approval.
   */
  flush: () => Promise<void>;
  saveStatus: CoordinatedSaveStatus;
  /**
   * True while a changed value is armed/pending or a write is in flight —
   * i.e. there is a local edit the server has not yet acknowledged. False
   * once the latest edit has been successfully persisted. Lets a consumer
   * gate an "adopt incoming server value" sync effect without reaching into
   * the hook's internal refs.
   */
  isGuardActive: boolean;
  /**
   * Re-seed the change-detection baseline to `value` without issuing a write.
   * Only a local save advances the baseline, so an external value adopted
   * while idle (WebSocket/refetch) would otherwise leave the baseline pinned
   * at the mount-time value — a later revert to the current server value then
   * issues a spurious no-op write. Call this from the sync effect whenever it
   * adopts a value that did not originate from a local save.
   */
  syncSavedBaseline: (value: T) => void;
}

export function useCoordinatedAutoSave<T>({
  registrationId,
  onSave,
  initialSavedValue,
  enabled = true,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseCoordinatedAutoSaveOptions<T>): UseCoordinatedAutoSaveResult<T> {
  const coordination = useAutoSaveCoordinationOptional();

  // Latest-refs: the registered flush and the armed timer must always reach the
  // current logic without forcing a re-registration or a re-arm.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const handleRef = useRef<CoordinationHandle | null>(null);
  /** Serialised payload of the last write that succeeded. */
  const lastSavedRef = useRef<string | null>(
    initialSavedValue === undefined ? null : JSON.stringify(initialSavedValue)
  );
  /** Newest value handed to `save`, and whether it still needs persisting. */
  const pendingValueRef = useRef<T | null>(null);
  const hasPendingRef = useRef(false);
  /** Set by onError so `flush` can reject on a failure useAutoSave swallowed. */
  const lastErrorRef = useRef<Error | null>(null);
  /**
   * Tail of the serialized write chain — settles when the last write currently
   * in flight or queued finishes, whichever path started it. Never rejects, so
   * awaiting it is only a barrier — failures come back through `lastErrorRef`.
   * Every write (debounced timer fire and explicit flush alike) chains onto it,
   * so two writes of the same action never overlap: a fast toggle whose save
   * fires while the mount-sync write is still open queues behind it instead of
   * issuing a second, concurrent write of a now-superseded action id. Also lets
   * `flush` wait out the whole queue instead of racing a save it could not see.
   */
  const inFlightRef = useRef<Promise<void> | null>(null);
  /**
   * Monotonic id stamped on each queued write in call order. A write owns the
   * pending flag only while it is the latest one queued: writes run strictly
   * one-at-a-time, so an older write's failure resolves *after* a newer write
   * has already been queued (and has already cleared `hasPendingRef` up front).
   * Without this guard the older failure would re-set `hasPendingRef`, and the
   * newer write's success — which does not touch the flag — would leave it stuck
   * true, making the next flush re-send a payload the newer write already
   * persisted and mint a duplicate action version.
   */
  const writeSeqRef = useRef(0);

  const setCoordinationState = useCallback((state: CoordinationSaveState) => {
    handleRef.current?.setSaveState(state);
  }, []);

  const persist = useCallback(
    (value: T): Promise<void> => {
      // Cleared up front: this value is now being committed, so a concurrent
      // flush joins the queue rather than starting its own write.
      hasPendingRef.current = false;
      const seq = (writeSeqRef.current += 1);

      // Serialize behind any write already in flight or queued. Without this, a
      // fast toggle whose (0ms-debounced) save fires while the mount-sync write
      // is still open would issue a second, overlapping write — and because the
      // first write mints a new action version, that second write targets a
      // superseded id and fails with the stale-action 409 this coordination
      // exists to prevent. Chaining runs the writes strictly one-at-a-time; the
      // renderer adopts the server's new action id before each write resolves,
      // so the queued write targets the current version.
      const prior = inFlightRef.current ?? Promise.resolve();
      const done = prior.then(async () => {
        // Re-check the freeze at queue-execution time. This write may have been
        // queued behind an earlier one before approval began; by the time that
        // earlier write settles and this callback runs, the approve path can
        // have frozen autosave. Issuing `update_payload` now would race the
        // approve POST (or be rejected once the proposal has left `pending`).
        // Drop it and settle coordination back to idle — the same thing the
        // fire-time `shouldSave` gate does for a timer that fires post-freeze.
        if (coordination?.isAutoSaveFrozen() === true) {
          hasPendingRef.current = false;
          setCoordinationState('idle');
          return;
        }
        setCoordinationState('saving');
        try {
          await onSaveRef.current(value);
          lastSavedRef.current = JSON.stringify(value);
        } catch (error) {
          // Keep the value pending so an explicit flush (Approve) retries it
          // rather than proceeding with a payload the server never received —
          // but only while both hold:
          //   1. This is still the latest queued write. A newer save queued
          //      behind this one already cleared the flag and owns it; if that
          //      newer write succeeds it never re-clears the flag, so
          //      resurrecting it here would strand `hasPendingRef` true and make
          //      the next flush re-send an already-persisted payload.
          //   2. The current pending value still differs from what is stored.
          //      If the user reverted to the stored value while this write was
          //      in flight (`save` re-arms but does not advance the seq), the
          //      seq check alone would still match and resurrect the flag — and
          //      the flush would then write a payload equal to what the server
          //      already has, minting a duplicate action version. Comparing
          //      against `lastSavedRef` (the same change-detection guard `save`
          //      and `shouldSave` use) keeps the reverted value from being
          //      retried.
          if (
            seq === writeSeqRef.current &&
            pendingValueRef.current !== null &&
            JSON.stringify(pendingValueRef.current) !== lastSavedRef.current
          ) {
            hasPendingRef.current = true;
          }
          throw error;
        }
      });

      // The tail is a non-rejecting barrier so one failed write cannot poison
      // the queue for the next; the failure still reaches the caller through
      // `done` (and onError/lastErrorRef). `flush` awaits this tail to block
      // approval behind the whole queue.
      const barrier = done.then(
        () => undefined,
        () => undefined
      );
      inFlightRef.current = barrier;
      void barrier.then(() => {
        if (inFlightRef.current === barrier) {
          inFlightRef.current = null;
        }
      });
      return done;
    },
    [coordination, setCoordinationState]
  );

  const handleSuccess = useCallback(() => {
    lastErrorRef.current = null;
    // A newer edit can arrive while this write is in flight; it stays armed and
    // unpersisted (hasPendingRef true). Reporting idle here would make the
    // approval footers — which gate flush on the aggregate pending/saving state —
    // skip flushAllAndWait and approve with the just-saved, now-stale payload
    // instead of the newer edit. Keep the rail pending until that edit lands.
    //
    // But a newer edit that reverted back to the value this write just persisted
    // is not actually pending: `save` could not detect it as unchanged because
    // the baseline still lagged behind the in-flight write. Leaving the flag set
    // would report the rail pending and make the approval flush issue a
    // duplicate, no-op `update_payload` write of the value already stored. Only
    // treat the edit as pending while it still differs from what we just saved.
    const stillPending =
      hasPendingRef.current &&
      JSON.stringify(pendingValueRef.current) !== lastSavedRef.current;
    hasPendingRef.current = stillPending;
    setCoordinationState(stillPending ? 'pending' : 'idle');
  }, [setCoordinationState]);

  const handleError = useCallback(
    (error: Error) => {
      lastErrorRef.current = error;
      setCoordinationState('error');
    },
    [setCoordinationState]
  );

  /**
   * Fire-time gate. Drops an armed save when approval has since frozen
   * autosave, or when the pending value no longer differs from what is already
   * stored (the user reverted it). Settles coordination back to idle so a
   * suppressed save cannot leave the aggregate stuck reporting `pending`.
   */
  const shouldSave = useCallback(() => {
    const suppress =
      coordination?.isAutoSaveFrozen() === true ||
      (pendingValueRef.current !== null &&
        JSON.stringify(pendingValueRef.current) === lastSavedRef.current);
    if (!suppress) {
      return true;
    }
    hasPendingRef.current = false;
    setCoordinationState('idle');
    return false;
  }, [coordination, setCoordinationState]);

  const {
    save: armSave,
    saveNow,
    cancelPendingSave,
    saveStatus,
  } = useAutoSave<T>(persist, {
    debounceMs,
    enabled,
    // save() is called from commit/change handlers, not from a mount effect —
    // swallowing the first call would drop the user's first edit.
    skipFirstSave: false,
    shouldSave,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const save = useCallback(
    (value: T) => {
      if (!enabledRef.current) {
        return;
      }
      // Arm-time freeze: approval has started, so a new timer must not be set.
      if (coordination?.isAutoSaveFrozen()) {
        return;
      }
      pendingValueRef.current = value;
      const isUnchanged = JSON.stringify(value) === lastSavedRef.current;
      hasPendingRef.current = !isUnchanged;
      setCoordinationState(isUnchanged ? 'idle' : 'pending');
      // Re-arm either way: a revert must supersede the armed value so the
      // discarded text is never the thing that gets written.
      armSave(value);
    },
    [armSave, coordination, setCoordinationState]
  );

  const flush = useCallback(async (): Promise<void> => {
    if (!enabledRef.current) {
      return;
    }
    // Drain the whole serialized write chain, not just its current tail. A
    // write can be appended to the tail (a 0ms-debounced timer firing) while we
    // await an earlier one, so awaiting `inFlightRef.current` a single time can
    // return while a later write is still queued — and that late `update_payload`
    // would then race the approve POST this flush runs right before. Keep
    // awaiting until the tail stops advancing (its settle handler clears the ref
    // once nothing newer has chained on).
    while (inFlightRef.current) {
      const tail = inFlightRef.current;
      await tail;
      if (inFlightRef.current === tail) {
        break;
      }
    }
    if (hasPendingRef.current) {
      lastErrorRef.current = null;
      // saveNow cancels the debounce and swallows errors into saveStatus;
      // lastErrorRef carries the failure back out to the caller.
      await saveNow(pendingValueRef.current as T);
    } else if (
      pendingValueRef.current !== null &&
      JSON.stringify(pendingValueRef.current) === lastSavedRef.current
    ) {
      // Nothing is pending because the newest value the user asked for is
      // already what the server holds — e.g. they reverted to the stored value
      // while an earlier write of the abandoned value was still in flight and
      // then failed. That failure left `lastErrorRef` set, but it belongs to a
      // payload the user discarded; blocking approval on it would fail the
      // approve for a change that no longer exists. Clear it so approval
      // proceeds against the value the server already has.
      lastErrorRef.current = null;
    }
    if (lastErrorRef.current) {
      throw lastErrorRef.current;
    }
  }, [saveNow]);

  const flushRef = useRef(flush);
  flushRef.current = flush;

  // Register with the coordination provider (when one is mounted) so the
  // approve rail can flush this editor. Keyed only on the registration
  // identity — the flush indirection keeps a fresh handle from ever carrying
  // stale save logic.
  useEffect(() => {
    if (!coordination) {
      return;
    }
    const handle = coordination.register(registrationId);
    handleRef.current = handle;
    handle.setFlushFn(() => flushRef.current());
    return () => {
      handle.unregister();
      handleRef.current = null;
    };
  }, [coordination, registrationId]);

  const syncSavedBaseline = useCallback(
    (value: T) => {
      lastSavedRef.current = JSON.stringify(value);
      // A debounce may still be armed from a pre-adoption edit — most subtly an
      // edit-then-revert, which leaves `hasPendingRef` false (so the consumer's
      // guard reads idle and lets this run) yet re-armed the timer with the
      // reverted value. Reseeding only the baseline would let that armed value
      // fire through the now-stale `shouldSave` gate and clobber the value we
      // just adopted. Cancel the timer and drop any local pending value so the
      // adopted baseline stands.
      cancelPendingSave();
      pendingValueRef.current = null;
      hasPendingRef.current = false;
    },
    [cancelPendingSave]
  );

  const isGuardActive = hasPendingRef.current || inFlightRef.current !== null;

  return { save, flush, saveStatus, isGuardActive, syncSavedBaseline };
}
